import React, { useMemo, useState } from 'react';
import {
  Box, Heading, HStack, VStack, Button, Input, Table, Thead, Tbody, Tr, Th, Td,
  Card, CardBody, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, FormControl, FormLabel, Select, Textarea, IconButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Suppliers = () => {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', category: '', main_focus: '', contact_person: '', notes: '' });
  const [rateModal, setRateModal] = useState({ open: false, supplierId: null });
  const [newRate, setNewRate] = useState({ service_name: '', rate_type: 'hour', rate: '', currency: '', notes: '' });
  const [itemModal, setItemModal] = useState({ open: false, supplierId: null });
  const [newMap, setNewMap] = useState({ item_id: '', purchase_price: '', currency: '', uom: 'unit', min_qty: '', lead_time_days: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, supplierId: null, supplierName: '' });
  const { data: inventory } = useQuery('inventory_min', () => api.get('/api/inventory').then(res => res.data.data.items));

  // Service Categories query
  const { data: categoriesData } = useQuery(
    ['service-categories'],
    async () => {
      const response = await api.get('/api/services/categories');
      return response.data;
    }
  );

  const { data: suppliers } = useQuery('suppliers', () =>
    api.get('/api/suppliers').then(res => res.data.data.suppliers)
  );

  const filtered = useMemo(() => {
    let filteredSuppliers = (suppliers || []);
    
    // Search filter - tüm alanlarda arama
    if (search) {
      const searchLower = search.toLowerCase();
      filteredSuppliers = filteredSuppliers.filter(s => 
        (s.name?.toLowerCase().includes(searchLower)) ||
        (s.email?.toLowerCase().includes(searchLower)) ||
        (s.phone?.toLowerCase().includes(searchLower)) ||
        (s.address?.toLowerCase().includes(searchLower)) ||
        (s.category?.toLowerCase().includes(searchLower)) ||
        (s.main_focus?.toLowerCase().includes(searchLower)) ||
        (s.contact_person?.toLowerCase().includes(searchLower)) ||
        (s.notes?.toLowerCase().includes(searchLower))
      );
    }
    
    // Category filter
    if (categoryFilter) {
      filteredSuppliers = filteredSuppliers.filter(s => 
        s.category === categoryFilter
      );
    }
    
    // Sorting
    filteredSuppliers.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filteredSuppliers;
  }, [suppliers, search, categoryFilter, sortBy, sortOrder]);

  // Service Categories for dropdown
  const categories = categoriesData?.data?.categories || [];
  const categoryNames = categories.map((c) => {
    const displayName = currentLanguage === 'de' ? (c.name_de || c.name_en) : (c.name_en || c.name_de);
    return { value: c.name_de || c.name_en, label: displayName };
  }).filter(cat => cat.value);

  const createSupplier = useMutation((data) => api.post('/api/suppliers', data), {
    onSuccess: () => { queryClient.invalidateQueries('suppliers'); setIsOpen(false); setForm({ name: '', email: '', phone: '', address: '', category: '', main_focus: '', contact_person: '', notes: '' }); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const updateSupplier = useMutation(({ id, data }) => api.put(`/api/suppliers/${id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries('suppliers'); setIsOpen(false); setEditing(null); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const deleteSupplier = useMutation((id) => api.delete(`/api/suppliers/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('suppliers'); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });

  const { data: rates } = useQuery(['supplier_rates', rateModal.supplierId], () =>
    api.get(`/api/suppliers/${rateModal.supplierId}/rates`).then(res => res.data.data.rates),
    { enabled: !!rateModal.supplierId }
  );

  const createRate = useMutation((payload) => api.post(`/api/suppliers/${rateModal.supplierId}/rates`, payload), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_rates', rateModal.supplierId]); setNewRate({ service_name: '', rate_type: 'hour', rate: '', currency: '', notes: '' }); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const updateRate = useMutation(({ id, data }) => api.put(`/api/suppliers/${rateModal.supplierId}/rates/${id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_rates', rateModal.supplierId]); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const deleteRate = useMutation((id) => api.delete(`/api/suppliers/${rateModal.supplierId}/rates/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_rates', rateModal.supplierId]); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });

  const { data: supplierItems } = useQuery(['supplier_items', itemModal.supplierId], () =>
    api.get(`/api/suppliers/${itemModal.supplierId}/items`).then(res => res.data.data.items),
    { enabled: !!itemModal.supplierId }
  );
  const createMap = useMutation((payload) => api.post(`/api/suppliers/${itemModal.supplierId}/items`, payload), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_items', itemModal.supplierId]); setNewMap({ item_id: '', purchase_price: '', currency: '', uom: 'unit', min_qty: '', lead_time_days: '' }); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const updateMap = useMutation(({ id, data }) => api.put(`/api/suppliers/${itemModal.supplierId}/items/${id}`, data), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_items', itemModal.supplierId]); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });
  const deleteMap = useMutation((id) => api.delete(`/api/suppliers/${itemModal.supplierId}/items/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries(['supplier_items', itemModal.supplierId]); toast({ title: t('common.success'), status: 'success' }); },
    onError: () => toast({ title: t('common.error'), status: 'error' }),
  });

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', phone: '', address: '', category: '', main_focus: '', contact_person: '', notes: '' }); setIsOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name || '', email: s.email || '', phone: s.phone || '', address: s.address || '', category: s.category || '', main_focus: s.main_focus || '', contact_person: s.contact_person || '', notes: s.notes || '' }); setIsOpen(true); };

  const saveSupplier = () => {
    if (!form.name) { toast({ title: t('common.error'), description: 'Name required', status: 'error' }); return; }
    if (editing) updateSupplier.mutate({ id: editing.id, data: form }); else createSupplier.mutate(form);
  };

  const handleDeleteSupplier = (supplier) => {
    setDeleteConfirm({ 
      isOpen: true, 
      supplierId: supplier.id, 
      supplierName: supplier.name 
    });
  };

  const confirmDeleteSupplier = () => {
    deleteSupplier.mutate(deleteConfirm.supplierId);
    setDeleteConfirm({ isOpen: false, supplierId: null, supplierName: '' });
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>{t('suppliers.title') || 'Suppliers'}</Heading>

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} wrap="wrap">
              <FormControl maxW="400px">
                <FormLabel fontSize="sm">{t('common.search', {defaultValue: 'Search'})}</FormLabel>
                <Input 
                  placeholder={t('suppliers.search_placeholder', {defaultValue: 'Search by name, email, address, category, main focus, contact person...'})}
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">{t('suppliers.filter_by_category', {defaultValue: 'Filter by Category'})}</FormLabel>
                <Select 
                  placeholder={t('suppliers.all_categories', {defaultValue: 'All Categories'})}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  {categoryNames.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl maxW="150px">
                <FormLabel fontSize="sm">{t('suppliers.sort_by', {defaultValue: 'Sort by'})}</FormLabel>
                <Select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name">{t('common.name')}</option>
                  <option value="address">{t('common.address')}</option>
                  <option value="category">{t('common.category')}</option>
                  <option value="main_focus">{t('suppliers.main_focus')}</option>
                  <option value="contact_person">{t('suppliers.contact_person')}</option>
                </Select>
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel fontSize="sm">{t('suppliers.order', {defaultValue: 'Order'})}</FormLabel>
                <Select 
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="asc">{t('suppliers.ascending', {defaultValue: 'A-Z'})}</option>
                  <option value="desc">{t('suppliers.descending', {defaultValue: 'Z-A'})}</option>
                </Select>
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel fontSize="sm">&nbsp;</FormLabel>
                <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate} w="100%">{t('common.new') || 'New'}</Button>
              </FormControl>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('common.name')}</Th>
                <Th>{t('common.address') || 'Address'}</Th>
                <Th>{t('common.category') || 'Category'}</Th>
                <Th>{t('suppliers.main_focus')}</Th>
                <Th>{t('suppliers.contact_person')}</Th>
                <Th>{t('common.email')}</Th>
                <Th>{t('common.phone')}</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered?.map(s => (
                <Tr key={s.id}>
                  <Td>{s.name}</Td>
                  <Td>{s.address || '-'}</Td>
                  <Td>{s.category || '-'}</Td>
                  <Td>{s.main_focus || '-'}</Td>
                  <Td>{s.contact_person || '-'}</Td>
                  <Td>{s.email}</Td>
                  <Td>{s.phone || '-'}</Td>
                  <Td>
                    <HStack>
                      <Button size="xs" variant="outline" onClick={() => openEdit(s)}>{t('common.edit')}</Button>
                      <Button size="xs" variant="outline" onClick={() => setRateModal({ open: true, supplierId: s.id })}>{t('suppliers.rates') || 'Rates'}</Button>
                      <Button size="xs" variant="outline" onClick={() => setItemModal({ open: true, supplierId: s.id })}>{t('suppliers.items') || 'Items'}</Button>
                      <IconButton aria-label="delete" size="xs" colorScheme="red" icon={<FiTrash2 />} onClick={() => handleDeleteSupplier(s)} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Create/Edit Supplier Modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? t('common.edit') : t('common.new')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.email')}</FormLabel>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.phone')}</FormLabel>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.address') || 'Address'}</FormLabel>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('suppliers.contact_person')}</FormLabel>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.category') || 'Category'}</FormLabel>
                  <Select 
                    value={form.category} 
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder={t('common.select_category', {defaultValue: 'Select category'})}
                  >
                    {categoryNames.map(category => (
                      <option key={category.value} value={category.value}>{category.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>{t('suppliers.main_focus')}</FormLabel>
                  <Input value={form.main_focus} onChange={(e) => setForm({ ...form, main_focus: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.notes') || 'Notes'}</FormLabel>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => setIsOpen(false)}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={saveSupplier} isLoading={createSupplier.isLoading || updateSupplier.isLoading}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Supplier Rates Modal */}
      <Modal isOpen={rateModal.open} onClose={() => setRateModal({ open: false, supplierId: null })} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('suppliers.rates') || 'Rates'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <FormControl>
                  <FormLabel>{t('common.service') || 'Service'}</FormLabel>
                  <Input value={newRate.service_name} onChange={(e) => setNewRate({ ...newRate, service_name: e.target.value })} />
                </FormControl>
                <FormControl maxW="180px">
                  <FormLabel>Type</FormLabel>
                  <Select size="sm" value={newRate.rate_type} onChange={(e) => setNewRate({ ...newRate, rate_type: e.target.value })}>
                    <option value="hour">hour</option>
                    <option value="day">day</option>
                    <option value="unit">unit</option>
                    <option value="m2">m2</option>
                    <option value="lm">lm</option>
                  </Select>
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel>Rate</FormLabel>
                  <Input type="number" value={newRate.rate} onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })} />
                </FormControl>
                <FormControl maxW="120px">
                  <FormLabel>Curr</FormLabel>
                  <Input value={newRate.currency} onChange={(e) => setNewRate({ ...newRate, currency: e.target.value })} />
                </FormControl>
                <Button leftIcon={<FiPlus />} onClick={() => createRate.mutate(newRate)}>{t('common.add')}</Button>
              </HStack>

              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('common.service') || 'Service'}</Th>
                    <Th>Type</Th>
                    <Th isNumeric>Rate</Th>
                    <Th>Curr</Th>
                    <Th>{t('common.notes') || 'Notes'}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rates?.map(r => (
                    <Tr key={r.id}>
                      <Td><Input size="sm" defaultValue={r.service_name} onBlur={(e) => updateRate.mutate({ id: r.id, data: { service_name: e.target.value } })} /></Td>
                      <Td>
                        <Select size="sm" defaultValue={r.rate_type} onChange={(e) => updateRate.mutate({ id: r.id, data: { rate_type: e.target.value } })}>
                          <option value="hour">hour</option>
                          <option value="day">day</option>
                          <option value="unit">unit</option>
                          <option value="m2">m2</option>
                          <option value="lm">lm</option>
                        </Select>
                      </Td>
                      <Td isNumeric><Input size="sm" type="number" defaultValue={r.rate} onBlur={(e) => updateRate.mutate({ id: r.id, data: { rate: e.target.value } })} /></Td>
                      <Td><Input size="sm" defaultValue={r.currency} onBlur={(e) => updateRate.mutate({ id: r.id, data: { currency: e.target.value } })} /></Td>
                      <Td><Textarea size="sm" defaultValue={r.notes || ''} onBlur={(e) => updateRate.mutate({ id: r.id, data: { notes: e.target.value } })} /></Td>
                      <Td><IconButton aria-label="delete" size="sm" colorScheme="red" icon={<FiTrash2 />} onClick={() => deleteRate.mutate(r.id)} /></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setRateModal({ open: false, supplierId: null })}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Supplier Items Modal */}
      <Modal isOpen={itemModal.open} onClose={() => setItemModal({ open: false, supplierId: null })} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('suppliers.items') || 'Items'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <FormControl maxW="300px">
                  <FormLabel>{t('inventory.title') || 'Item'}</FormLabel>
                  <Select size="sm" value={newMap.item_id} onChange={(e) => setNewMap({ ...newMap, item_id: e.target.value })}>
                    <option value="">{t('common.select') || 'Select'}</option>
                    {inventory?.map(it => (
                      <option key={it.id} value={it.id}>{it.name}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel>Price</FormLabel>
                  <Input type="number" value={newMap.purchase_price} onChange={(e) => setNewMap({ ...newMap, purchase_price: e.target.value })} />
                </FormControl>
                <FormControl maxW="120px">
                  <FormLabel>Curr</FormLabel>
                  <Input value={newMap.currency} onChange={(e) => setNewMap({ ...newMap, currency: e.target.value })} />
                </FormControl>
                <FormControl maxW="120px">
                  <FormLabel>UoM</FormLabel>
                  <Input value={newMap.uom} onChange={(e) => setNewMap({ ...newMap, uom: e.target.value })} />
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel>Min Qty</FormLabel>
                  <Input type="number" value={newMap.min_qty} onChange={(e) => setNewMap({ ...newMap, min_qty: e.target.value })} />
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel>Lead (days)</FormLabel>
                  <Input type="number" value={newMap.lead_time_days} onChange={(e) => setNewMap({ ...newMap, lead_time_days: e.target.value })} />
                </FormControl>
                <Button leftIcon={<FiPlus />} onClick={() => createMap.mutate(newMap)}>{t('common.add')}</Button>
              </HStack>

              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('common.name')}</Th>
                    <Th>SKU</Th>
                    <Th isNumeric>Price</Th>
                    <Th>Curr</Th>
                    <Th>UoM</Th>
                    <Th isNumeric>Min Qty</Th>
                    <Th isNumeric>Lead</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {supplierItems?.map(m => (
                    <Tr key={m.id}>
                      <Td>{m.item_name}</Td>
                      <Td>{m.sku}</Td>
                      <Td isNumeric><Input size="sm" type="number" defaultValue={m.purchase_price} onBlur={(e) => updateMap.mutate({ id: m.id, data: { purchase_price: e.target.value } })} /></Td>
                      <Td><Input size="sm" defaultValue={m.currency} onBlur={(e) => updateMap.mutate({ id: m.id, data: { currency: e.target.value } })} /></Td>
                      <Td><Input size="sm" defaultValue={m.uom} onBlur={(e) => updateMap.mutate({ id: m.id, data: { uom: e.target.value } })} /></Td>
                      <Td isNumeric><Input size="sm" type="number" defaultValue={m.min_qty} onBlur={(e) => updateMap.mutate({ id: m.id, data: { min_qty: e.target.value } })} /></Td>
                      <Td isNumeric><Input size="sm" type="number" defaultValue={m.lead_time_days} onBlur={(e) => updateMap.mutate({ id: m.id, data: { lead_time_days: e.target.value } })} /></Td>
                      <Td><IconButton aria-label="delete" size="sm" colorScheme="red" icon={<FiTrash2 />} onClick={() => deleteMap.mutate(m.id)} /></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setItemModal({ open: false, supplierId: null })}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, supplierId: null, supplierName: '' })}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {t('common.confirm_delete')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('suppliers.confirm_delete_message')} "{deleteConfirm.supplierName}"?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setDeleteConfirm({ isOpen: false, supplierId: null, supplierName: '' })}>
                {t('common.cancel')}
              </Button>
              <Button 
                colorScheme="red" 
                onClick={confirmDeleteSupplier} 
                ml={3}
                isLoading={deleteSupplier.isLoading}
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Suppliers;

